import {
  format,
  parseISO,
  formatDistanceToNow,
  differenceInMonths,
  isAfter,
  isBefore,
  isSameDay,
  addYears,
  addMonths,
  startOfDay,
  getDay,
  getMonth,
  getYear,
  getWeek,
  getUnixTime,
  compareDesc,
} from "date-fns";
import { sv } from "date-fns/locale";

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "string") return parseISO(value);
  return new Date(value);
}

export function formatDate(value, pattern) {
  const date = toDate(value);
  if (!date || isNaN(date.getTime())) return "";
  if (!pattern) {
    throw new Error("formatDate requires a pattern");
  }
  return format(date, pattern, { locale: sv });
}

export function fromNow(value) {
  const date = toDate(value);
  if (!date || isNaN(date.getTime())) return "";
  return formatDistanceToNow(date, { addSuffix: true, locale: sv });
}

export function diffMonths(dateA, dateB) {
  const a = toDate(dateA);
  const b = toDate(dateB);
  if (!a || !b) return NaN;
  return differenceInMonths(a, b);
}

export function dateIsAfter(value, other) {
  const date = toDate(value);
  if (!date) return false;
  const otherDate = other ? toDate(other) : new Date();
  return isAfter(date, otherDate);
}

export function dateIsSameOrAfter(value, other) {
  const date = toDate(value);
  const otherDate = toDate(other);
  if (!date || !otherDate) return false;
  return !isBefore(startOfDay(date), startOfDay(otherDate));
}

export function dateIsSameDay(value, other) {
  const a = toDate(value);
  const b = toDate(other);
  if (!a || !b) return false;
  return isSameDay(a, b);
}

export function dateAddYears(value, years) {
  return addYears(toDate(value) || new Date(), years);
}

export function dateAddMonths(value, months) {
  return addMonths(toDate(value) || new Date(), months);
}

export function dateGetMonth(value) {
  const date = toDate(value);
  if (!date) return NaN;
  return getMonth(date);
}

export function dateGetYear(value) {
  const date = toDate(value);
  if (!date) return NaN;
  return getYear(date);
}

export function dateGetWeek(value) {
  const date = toDate(value);
  if (!date) return NaN;
  return getWeek(date, { locale: sv });
}

export function dateGetDay(value) {
  const date = toDate(value);
  if (!date) return NaN;
  return getDay(date);
}

export function dateGetUnixTime(value) {
  const date = toDate(value);
  if (!date) return NaN;
  return getUnixTime(date);
}

export function compareDatesDesc(a, b) {
  const dateA = toDate(a);
  const dateB = toDate(b);
  if (!dateA && !dateB) return 0;
  if (!dateA) return 1;
  if (!dateB) return -1;
  return compareDesc(dateA, dateB);
}

export { toDate };
